import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { TrendingUp, ClipboardList, Users, Package, AlertTriangle } from "lucide-react";
import { API, inr, logErr } from "../../lib/api";

export default function AdminDashboard() {
  const [d, setD] = useState(null);

  useEffect(() => {
    document.title = "Admin Dashboard | Dressedin";
    API.get("/admin/dashboard").then((r) => setD(r.data)).catch((err) => logErr("Failed to load dashboard:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!d) return <div className="text-muted-foreground">Loading dashboard...</div>;

  const stats = [
    { label: "Revenue (30d)", value: inr(d.revenue_30d), icon: TrendingUp, testid: "stat-revenue" },
    { label: "Orders (30d)", value: d.orders_30d, icon: ClipboardList, testid: "stat-orders" },
    { label: "Customers", value: d.customers, icon: Users, testid: "stat-customers" },
    { label: "Products", value: d.products, icon: Package, testid: "stat-products" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-display font-black text-3xl tracking-tighter mb-8">Dashboard</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {stats.map(({ label, value, icon: Icon, testid }) => (
          <div key={label} className="border border-[#262626] bg-ink-surface p-5" data-testid={testid}>
            <div className="flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
              <Icon size={15} className="text-gold" />
            </div>
            <p className="font-display font-black text-2xl mt-2 gold-text">{value}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 border border-[#262626] bg-ink-surface p-5" data-testid="revenue-chart">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">Revenue — last 14 days</p>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={d.revenue_by_day}>
              <defs>
                <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" stroke="#52525b" fontSize={10} tickLine={false} axisLine={false} />
              <YAxis stroke="#52525b" fontSize={10} tickLine={false} axisLine={false} tickFormatter={(v) => `₹${v >= 1000 ? `${v / 1000}k` : v}`} />
              <Tooltip contentStyle={{ background: "#121212", border: "1px solid #262626", fontSize: 12 }}
                labelStyle={{ color: "#D4AF37" }} formatter={(v) => [inr(v), "Revenue"]} />
              <Area type="monotone" dataKey="revenue" stroke="#D4AF37" strokeWidth={2} fill="url(#goldFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-[#262626] bg-ink-surface p-5" data-testid="best-sellers">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">Best sellers</p>
          {d.best_sellers.length === 0 && <p className="text-xs text-muted-foreground">No sales yet.</p>}
          <div className="space-y-3">
            {d.best_sellers.map((b, i) => (
              <div key={b.name} className="flex items-center gap-3">
                <span className="font-display font-black text-gold text-lg w-6">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold truncate">{b.name}</p>
                  <p className="text-[10px] text-muted-foreground">{b.qty} sold · {inr(b.revenue)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="border border-[#262626] bg-ink-surface p-5" data-testid="low-stock-alerts">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4 flex items-center gap-2">
            <AlertTriangle size={13} className="text-gold" /> Low stock alerts
          </p>
          {d.low_stock.length === 0 && <p className="text-xs text-muted-foreground">All stocked up.</p>}
          <div className="space-y-2">
            {d.low_stock.map((p) => (
              <Link key={p.id} to="/admin/inventory" className="flex items-center justify-between text-xs border-b border-[#262626] pb-2 hover:text-gold transition-colors">
                <span className="truncate">{p.name}</span>
                <span className={`font-bold ${p.stock === 0 ? "text-red-400" : "text-gold"}`}>{p.stock === 0 ? "OUT" : `${p.stock} left`}</span>
              </Link>
            ))}
          </div>
        </div>

        <div className="border border-[#262626] bg-ink-surface p-5" data-testid="recent-orders">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Recent orders</p>
            <span className="text-[10px] text-gold font-bold">{d.pending_orders} pending</span>
          </div>
          {d.recent_orders.length === 0 && <p className="text-xs text-muted-foreground">No orders yet.</p>}
          <div className="space-y-2">
            {d.recent_orders.map((o) => (
              <Link key={o.id} to="/admin/orders" className="flex items-center justify-between text-xs border-b border-[#262626] pb-2 hover:text-gold transition-colors">
                <span>{o.order_number}</span>
                <span className="text-muted-foreground">{o.status.replace(/_/g, " ")}</span>
                <span className="font-bold">{inr(o.total)}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
